// httpInterceptor.ts
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, concatMap, take } from 'rxjs/operators';
import { REGION_CTX } from '@app/areas/auth/injectables/region-token';
import { RegionService } from '@app/areas/auth/services/region.service';
import { AuthService } from '@app/areas/auth/services/auth.service';
import { OtpAuthService } from '@app/areas/auth/services/otp-auth.service';
import { AlertService } from '@services/helpers/alert/alert.service';
import { environment } from '@environments/environment';
import { DeviceIdService } from '@src/app/areas/device/services/device-id.service';
import { APP_REQUEST_SCOPE_CTX } from '@app/shared/services/core/local-scope/local-scope.types';
import { logLocalScopeDebug } from '@app/shared/services/core/local-scope/local-scope-debug';
import { LocalizeService } from '@shared/services/helpers/localize/localize.service';
import { ErrorBreadcrumbsService } from '@app/areas/crm/support-cases/services/error-breadcrumbs.service';

@Injectable({ providedIn: 'root' })
export class DeviceInfoService {
  getModel(): string {
    return 'browser';
  }

  getOsVersion(): string {
    return navigator.userAgent;
  }

  getPlatform(): string {
    return 'web';
  }
}

@Injectable()
export class CustomInterceptor implements HttpInterceptor {
  constructor(
    private deviceIdService: DeviceIdService,
    private deviceInfo: DeviceInfoService,
    private alert: AlertService,
    private regionService: RegionService,
    private localizeService: LocalizeService,
    private injector: Injector
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let headers = req.headers;

    const auth = this.injector.get(AuthService, null as any);
    const otp  = this.injector.get(OtpAuthService, null as any);

    const at = auth?.accessToken ?? null;
    const otpAt = otp?.accessToken$() ?? null;

    const isApi = req.url.includes('api');
    const isLogout = req.url.includes('logout');

    // -------------------------
    // Device Headers
    // -------------------------

    const deviceId = this.deviceIdService.get();
    const model = this.deviceInfo.getModel();           // "browser"
    const osVersion = this.deviceInfo.getOsVersion();   // userAgent or parsed version
    const platform = this.deviceInfo.getPlatform();     // "web"
    const summary = `${model} (${osVersion})`;

    headers = headers
      .set('x-device-id', deviceId)
      .set('x-device', model)
      .set('x-os-version', osVersion)
      .set('x-platform', platform)
      .set('x-device-summary', summary)
      .set('x-app-version', environment.appVersion ?? 'unknown');

    // Debug
    // console.log("Device headers:", { deviceId, model, osVersion, platform, summary });

    // -------------------------
    // Auth headers
    // -------------------------
    if (isApi && !isLogout) {
      if (at) {
        headers = headers.set('Authorization', `Bearer ${at}`);
      } else if (otpAt) {
        headers = headers
          .set('x-otp-token', `${otpAt}`)
          .set('Authorization', `Bearer ${otpAt}`);
      }
    }

    // -------------------------
    // Region
    // -------------------------
    const requestScope = req.context.get(APP_REQUEST_SCOPE_CTX);
    const regionFromCtx = req.context.get(REGION_CTX);
    const globalRegion = this.regionService.get();
    const region = requestScope?.region ?? regionFromCtx ?? globalRegion;

    if (region) {
      headers = headers
        .set('x-fitmed-region', region);
    }

    const locale = this.localizeService.currentLocaleValue?.trim();
    if (locale) {
      headers = headers.set('x-locale', locale);
    }

    // -------------------------
    // Organisation context
    // -------------------------
    const selectedOrganisationId = requestScope?.organisationId ?? localStorage.getItem('selectedLegalEntityId');
    const selectedWorkspaceId = requestScope?.workspaceId ?? localStorage.getItem('selectedWorkspaceId');
    const selectedSiteId = requestScope?.siteId ?? null;

    if (selectedOrganisationId) {
      headers = headers.set('x-fitmed-oid', selectedOrganisationId);
    }

    if (selectedWorkspaceId) {
      headers = headers.set('x-fitmed-wid', selectedWorkspaceId);
    }

    if (selectedSiteId) {
      headers = headers.set('x-fitmed-sid', selectedSiteId);
    }

    logLocalScopeDebug('request-resolved', {
      url: req.url,
      requestScope: requestScope ?? null,
      legacyRegion: regionFromCtx ?? null,
      globalRegion,
      resolvedRegion: region ?? null,
      resolvedRegionSource: requestScope?.region
        ? 'request-scope'
        : regionFromCtx
          ? 'region-context'
          : globalRegion
            ? 'global-session'
            : 'none',
      organisationId: selectedOrganisationId ?? null,
      workspaceId: selectedWorkspaceId ?? null,
      siteId: selectedSiteId ?? null,
    });

    const request = req.clone({ withCredentials: true, headers });

    return next.handle(request).pipe(
      catchError((err: any): Observable<HttpEvent<any>> => {
        const is401 = err?.status === 401;
        const isRedirectedToLogin =
          (err?.status === 404 || err?.status === 302) &&
          ((typeof err?.url === 'string' && err.url.includes('/Account/Login')) ||
           req.url.includes('/Account/Login'));
        const isAuthEndpoint =
          req.url.includes('/auth/login') ||
          req.url.includes('/auth/refreshToken') ||
          req.url.includes('/auth/logout') ||
          req.url.includes('/auth/requestPhoneCode') ||
          req.url.includes('/auth/confirmPhoneCode') ||
          req.url.includes('/auth/setupPassword');
        const alreadyRetried = req.headers.has('x-refresh-retry');

        if ((is401 || isRedirectedToLogin) && isApi && !isAuthEndpoint && !alreadyRetried && auth) {
          return (auth.checkLoggedIn() as Observable<boolean>).pipe(
            take(1),
            concatMap((refreshed: boolean): Observable<HttpEvent<any>> => {
              if (!refreshed || !auth.accessToken) {
                return throwError(() => err);
              }
              const retriedHeaders = req.headers
                .set('Authorization', `Bearer ${auth.accessToken}`)
                .set('x-refresh-retry', '1');
              const retriedReq = req.clone({
                withCredentials: true,
                headers: retriedHeaders,
              });
              return next.handle(retriedReq);
            }),
            catchError((retryErr): Observable<HttpEvent<any>> => throwError(() => retryErr)),
          );
        }

        // Log error for monitoring
        console.error('HTTP Request failed:', err);

        try {
          const breadcrumbs = this.injector.get(ErrorBreadcrumbsService, null as any);
          breadcrumbs?.recordHttpError(req.url, err?.status ?? 0, err?.statusText);
        } catch {
          // ignore
        }

        // Allow components to handle specific errors locally
        return throwError(() => err);
      })
    );
  }
}
